import { Link, NavLink } from 'react-router';
import {
  CalendarClock,
  HomeIcon,
  LayoutDashboard,
  MessageSquareDot,
  CandlestickChart,
  Rocket,
  UsersIcon,
} from 'lucide-react';
import { PrimeLogo } from '@/assets/prime-logo';
import { useMediaQuery } from '@/hooks/use-media-query';
import {
  MNavbar,
  MNavbarItem,
  MNavbarList,
  MNavbarLogo,
  MNavbarMenu,
  MNavbarMenuCategoryList,
  MNavbarMenuItem,
} from './ui/m-navbar';
import { useSession } from '@/services/providers/session-provider.tsx';
import { useGetPlantsQuery } from '@/features/plants/hooks/queries/use-get-plants.tsx';
import { Button } from './ui/button';
import { usePlantsStore } from '@/services/store/use-plants-store.tsx';
import { cn } from '@/utils/utils.ts';
import {
  SidebarFooter,
  SidebarHeadLogo,
  SidebarHeadRoot,
  SidebarHeadToggle,
  SidebarNavRoot,
  SidebarNavRootSection,
  SidebarNavSectionItem,
  SidebarNavSectionTitle,
  SidebarRoot,
} from '@/components/sidebar.tsx';
import { FeedbackDialog } from '../features/feedback/components/feedback-dialog.tsx';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import { hasPermission } from '@/features/users/schemas/users.permissions.ts';
import { BESSIcon } from '@/assets/bess-icon.tsx';
import BessCircleStatus from '@/features/bess/components/bess-circle-status.tsx';
import { useGetAllBESSQuery } from '@/features/bess/hooks/queries/get-all-bess.ts';

const MAIN_MENU = [
  {
    id: 'dashboard',
    Icon: LayoutDashboard,
    translationKey: 'navigation.menu.dashboard',
    href: '/dashboard',
    action: 'navigation.main-menu.dashboard',
  },
  {
    id: 'plants',
    Icon: HomeIcon,
    translationKey: 'navigation.menu.plants',
    href: '/plants',
    action: 'navigation.main-menu.plants',
  },
  {
    id: 'bess',
    Icon: BESSIcon,
    translationKey: 'navigation.menu.bess',
    href: '/bess',
    action: 'navigation.main-menu.bess',
  },
] as const;

const TOOLS = [
  {
    id: 'energy-trading',
    Icon: CandlestickChart,
    translationKey: 'navigation.menu.energy-trading',
    href: '/trading',
    action: 'navigation.admin-menu.energy-trading',
  },
  {
    id: 'event-rules',
    Icon: CalendarClock,
    translationKey: 'navigation.tools.event_rules',
    href: '/tools/event-rules',
    action: 'navigation.tools.event-rules',
  },
  {
    id: 'notification-rules',
    Icon: MessageSquareDot,
    translationKey: 'navigation.tools.notification_rules',
    href: '/tools/notification-rules',
    action: 'navigation.tools.notification-rules',
  },
] as const;

const ADMIN_MENU = [
  {
    id: 'users',
    Icon: UsersIcon,
    translationKey: 'navigation.menu.users',
    href: '/users',
    action: 'navigation.admin-menu.users',
  },
] as const;

const canSeeTradingLink = (email?: string) => {
  if (!email) return false;

  const allowedUsers = [
    'catalin.petrovici@nokto.eu',
    'alex.paval@nokto.eu',
    'andrei.morosan@nokto.eu',
    'ciobanu.vicentiu@gmail.com',
  ];

  return allowedUsers.includes(email.toLowerCase());
};

export const Navigation = () => {
  const isMobile = useMediaQuery('(max-width:640px)');
  const { userId, role, email, isLoading } = useSession();
  const { t } = useTranslation();
  const { data: plants } = useGetPlantsQuery({
    enabled:
      hasPermission({ userId, role }, 'navigation', 'navigation.main-menu.plants') ||
      hasPermission(
        {
          userId,
          role,
        },
        'navigation',
        'navigation.plants'
      ),
  });

  const { data: bess } = useGetAllBESSQuery({
    enabled: hasPermission({ userId, role }, 'navigation', 'navigation.main-menu.bess'),
  });

  const [selectedPlant, setSelectedPlant] = usePlantsStore(
    useShallow((state) => [state.selectedPlant, state.setSelectedPlant])
  );

  if (!isMobile) {
    return (
      <SidebarRoot>
        <SidebarHeadRoot>
          <SidebarHeadLogo>
            <PrimeLogo
              className={`overflow-hidden transition-all duration-300`}
              width="128px"
              height="40px"
              withText={true}
            />
          </SidebarHeadLogo>
          <SidebarHeadToggle />
        </SidebarHeadRoot>

        <SidebarNavRoot>
          {hasPermission({ userId, role }, 'navigation', 'navigation.main-menu') && (
            <SidebarNavRootSection>
              <SidebarNavSectionTitle>{t('navigation.mainMenu')}</SidebarNavSectionTitle>
              {MAIN_MENU.filter(({ action }) =>
                hasPermission({ userId, role }, 'navigation', action)
              ).map(({ id, translationKey, Icon, href }) => {
                if (id === 'bess') {
                  return (
                    <SidebarNavSectionItem
                      key={id}
                      label={t(translationKey)}
                      icon={<BESSIcon />}
                      as="ul"
                    >
                      {bess?.map((p) => {
                        return (
                          <NavLink key={p.id} to={`bess/${p.id}`}>
                            {({ isActive }) => (
                              <SidebarNavSectionItem
                                label={p.name}
                                as="span"
                                active={isActive}
                                icon={<div />}
                                statusChildren={
                                  <BessCircleStatus status={p.mqttStatus}></BessCircleStatus>
                                }
                              />
                            )}
                          </NavLink>
                        );
                      })}
                    </SidebarNavSectionItem>
                  );
                }

                return (
                  <NavLink key={id} to={href}>
                    {({ isActive }) => (
                      <SidebarNavSectionItem
                        as="span"
                        label={t(translationKey)}
                        icon={<Icon />}
                        active={isActive}
                      />
                    )}
                  </NavLink>
                );
              })}
            </SidebarNavRootSection>
          )}

          {hasPermission({ userId, role }, 'navigation', 'navigation.plants') && (
            <SidebarNavRootSection>
              <SidebarNavSectionTitle>{t('navigation.plants')}</SidebarNavSectionTitle>
              {plants?.map((p) => {
                const isSelected = selectedPlant?.plantId === p.plantId;

                return (
                  <SidebarNavSectionItem
                    key={p.plantId}
                    label={p.basicDetails.name}
                    as="button"
                    onClick={() => {
                      setSelectedPlant(p);
                    }}
                    active={isSelected}
                    icon={<HomeIcon />}
                  />
                );
              })}
            </SidebarNavRootSection>
          )}

          {hasPermission({ userId, role }, 'navigation', 'navigation.tools') && (
            <SidebarNavRootSection>
              <SidebarNavSectionTitle>
                {t('navigation.section_title.tools')}
              </SidebarNavSectionTitle>
              {TOOLS.filter(
                ({ action, id }) =>
                  hasPermission({ userId, role }, 'navigation', action) &&
                  (id !== 'energy-trading' || canSeeTradingLink(email))
              ).map(({ id, translationKey, Icon, href }) => {
                return (
                  <NavLink key={id} to={href}>
                    {({ isActive }) => (
                      <SidebarNavSectionItem
                        as="span"
                        label={t(translationKey)}
                        icon={<Icon />}
                        active={isActive}
                      />
                    )}
                  </NavLink>
                );
              })}
            </SidebarNavRootSection>
          )}

          {hasPermission({ userId, role }, 'navigation', 'navigation.admin-menu') && (
            <SidebarNavRootSection>
              <SidebarNavSectionTitle>{t('navigation.adminMenu')}</SidebarNavSectionTitle>
              {ADMIN_MENU.filter(({ action }) =>
                hasPermission({ userId, role }, 'navigation', action)
              ).map(({ id, translationKey, Icon, href }) => {
                return (
                  <NavLink key={id} to={href}>
                    {({ isActive }) => (
                      <SidebarNavSectionItem
                        as="span"
                        label={t(translationKey)}
                        icon={<Icon />}
                        active={isActive}
                      />
                    )}
                  </NavLink>
                );
              })}
            </SidebarNavRootSection>
          )}
        </SidebarNavRoot>

        <SidebarFooter>
          <SidebarNavRootSection className="pt-1">
            {/*{hasPermission(*/}
            {/*  { userId, role },*/}
            {/*  'navigation',*/}
            {/*  'navigation.main-menu.release-notes'*/}
            {/*) && (*/}
            {/*  <Link to="release-notes">*/}
            {/*    <SidebarNavSectionItem*/}
            {/*      as="span"*/}
            {/*      label="Release notes"*/}
            {/*      icon={<NotebookIcon />}*/}
            {/*    />*/}
            {/*  </Link>*/}
            {/*)}*/}
            <FeedbackDialog>
              <SidebarNavSectionItem
                as="button"
                icon={<Rocket />}
                label={t('navigation.menu.feedback')}
              />
            </FeedbackDialog>
          </SidebarNavRootSection>
        </SidebarFooter>
      </SidebarRoot>
    );
  }

  return (
    <MNavbar>
      {/* Logo */}
      <MNavbarLogo>
        <Link to="/dashboard">
          <PrimeLogo height="26" width="75"></PrimeLogo>
        </Link>
      </MNavbarLogo>

      {/* Nav Icons */}
      <MNavbarList isLoading={isLoading}>
        {hasPermission({ userId, role }, 'navigation', 'navigation.main-menu') &&
          [...MAIN_MENU.slice(0, 2), ...ADMIN_MENU.slice(0, 1)]
            .filter(({ action }) => hasPermission({ userId, role }, 'navigation', action))
            .map(({ Icon, href }) => (
              <NavLink key={href} to={href}>
                {({ isActive }) => (
                  <MNavbarItem icon={<Icon width="24px" height="24px" />} active={isActive} />
                )}
              </NavLink>
            ))}
      </MNavbarList>

      {/* Menu */}
      <MNavbarMenu isLoading={isLoading}>
        {/* Logo */}
        <MNavbarLogo>
          <PrimeLogo height="36" width="100" />
        </MNavbarLogo>

        {hasPermission({ userId, role }, 'navigation', 'navigation.main-menu') && (
          <MNavbarMenuCategoryList category={t('navigation.mainMenu')}>
            {role &&
              [...MAIN_MENU.slice(0, 2)]
                .filter(({ action }) => hasPermission({ userId, role }, 'navigation', action))
                .map(({ translationKey, Icon, href }) => (
                  <Link key={href} to={href}>
                    <MNavbarMenuItem
                      text={t(translationKey)}
                      icon={<Icon width="24px" height="24px" />}
                    />
                  </Link>
                ))}
          </MNavbarMenuCategoryList>
        )}

        {hasPermission({ userId, role }, 'navigation', 'navigation.tools') && (
          <MNavbarMenuCategoryList category={t('navigation.section_title.tools')}>
            {role &&
              [...TOOLS]
                .filter(
                  ({ action, id }) =>
                    hasPermission({ userId, role }, 'navigation', action) &&
                    (id !== 'energy-trading' || canSeeTradingLink(email))
                )
                .map(({ translationKey, Icon, href }) => (
                  <Link key={href} to={href}>
                    <MNavbarMenuItem
                      text={t(translationKey)}
                      icon={<Icon width="24px" height="24px" />}
                    />
                  </Link>
                ))}
          </MNavbarMenuCategoryList>
        )}

        {hasPermission({ userId, role }, 'navigation', 'navigation.admin-menu') && (
          <MNavbarMenuCategoryList category={t('navigation.adminMenu')}>
            {role &&
              [...ADMIN_MENU.slice(0, 1)]
                .filter(({ action }) => hasPermission({ userId, role }, 'navigation', action))
                .map(({ translationKey, Icon, href }) => (
                  <Link key={href} to={href}>
                    <MNavbarMenuItem
                      text={t(translationKey)}
                      icon={<Icon width="24px" height="24px" />}
                    />
                  </Link>
                ))}
          </MNavbarMenuCategoryList>
        )}

        {hasPermission({ userId, role }, 'navigation', 'navigation.plants') && (
          <MNavbarMenuCategoryList category={t('navigation.menu.plants')}>
            {plants?.map((p) => {
              const isSelected = selectedPlant?.plantId === p.plantId;

              return (
                <Button
                  variant="ghost"
                  key={p.plantId}
                  onClick={() => {
                    setSelectedPlant(p);
                  }}
                  className={cn(
                    'my-1 justify-start p-0 text-start',
                    isSelected && 'bg-slate-200'
                  )}
                >
                  <MNavbarMenuItem text={p.basicDetails.name} icon={<HomeIcon />} />
                </Button>
              );
            })}
          </MNavbarMenuCategoryList>
        )}

        {/*{hasPermission(*/}
        {/*  { userId, role },*/}
        {/*  'navigation',*/}
        {/*  'navigation.main-menu.release-notes'*/}
        {/*) && (*/}
        {/*  <div className="border-t pt-4">*/}
        {/*    <Link to="release-notes">*/}
        {/*      <MNavbarMenuItem text="Release notes" icon={<NotebookIcon />} />*/}
        {/*    </Link>*/}
        {/*  </div>*/}
        {/*)}*/}
      </MNavbarMenu>
    </MNavbar>
  );
};
